import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDown, Clock, MoreHorizontal, SlidersHorizontal } from 'lucide-react';
import clsx from 'clsx';
import { NDTDashboard } from '../../../ndt/NDTAnalytics';
import { WeldDashboard } from '../../../weld-analytics/WeldAnalytics';
import { CombinedAnalyticsHost } from '../../../combined-analytics/CombinedAnalytics';
import { DashboardProject } from '../../types/project';
import { Label } from '../../../../components/ui/label';
import { MultiSelect, MultiSelectChips } from '../../../../components/ui/multi-select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../components/ui/custom-dropdown-menu';
import { getAllPipeDetails } from '../../../../redux/Project/setupData/action';
import type { PipeOption } from '../../../ndt/components/FilterPanel';

/**
 * Flip to true to restore Project Analytics sub-tabs
 * (NDT Analytics | Weld Analytics | Combined + Executive overflow).
 * Default: Combined-only Analytics with no sub-tab strip.
 */
const SHOW_LEGACY_ANALYTICS_VIEWS = false;

type AnalyticsView = 'ndt' | 'weld' | 'combined' | 'executive';

type ViewEntry = {
  id: AnalyticsView;
  label: string;
  comingSoon?: boolean;
};

const PRIMARY_VIEWS: ViewEntry[] = [
  { id: 'ndt', label: 'NDT Analytics' },
  { id: 'weld', label: 'Weld Analytics' },
  { id: 'combined', label: 'Combined' },
];

const OVERFLOW_VIEWS: ViewEntry[] = [
  { id: 'executive', label: 'Executive', comingSoon: true },
];

const COMING_SOON_COPY: Record<'executive', string> = {
  executive: 'High-level dashboard pulling headline KPIs from NDT, Welding, and combined signals.',
};

const ComingSoon = ({ title, description }: { title: string; description: string }) => (
  <div className="rounded-2xl border border-dashed border-[#E2E5EA] bg-white/60 p-12 text-center">
    <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[rgba(230,161,0,0.08)] text-[#B38600]">
      <Clock size={22} />
    </div>
    <h3 className="font-heading text-lg font-semibold text-blueprint-blue">{title} — Coming soon</h3>
    <p className="mx-auto mt-2 max-w-md text-sm text-heritage-grey">{description}</p>
  </div>
);

export const AnalyticsShell = ({ project }: { project: DashboardProject }) => {
  const dispatch = useDispatch();
  const [activeView, setActiveView] = useState<AnalyticsView>(
    SHOW_LEGACY_ANALYTICS_VIEWS ? 'ndt' : 'combined',
  );
  /** Draft selection while the pipe dropdown is open. */
  const [selectedPipeIds, setSelectedPipeIds] = useState<string[]>([]);
  /** Committed pipes — Analytics loads only when dropdown closes or chips are removed. */
  const [committedPipeIds, setCommittedPipeIds] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const selectedPipeIdsRef = useRef(selectedPipeIds);
  selectedPipeIdsRef.current = selectedPipeIds;

  const { pipeDetails } = useSelector((state: any) => state.setupData || {});
  const showPipeToolbar =
    !SHOW_LEGACY_ANALYTICS_VIEWS || activeView === 'weld' || activeView === 'combined';
  const showFiltersToggle =
    !SHOW_LEGACY_ANALYTICS_VIEWS || activeView === 'weld' || activeView === 'combined';

  const projectId = project?.project?.projectId || null;

  const lastFetchedProjectIdRef = useRef<string | number | null>(null);
  const defaultPipeAppliedRef = useRef<boolean>(false);

  useEffect(() => {
    if (projectId !== null && projectId !== lastFetchedProjectIdRef.current) {
      lastFetchedProjectIdRef.current = projectId;
      defaultPipeAppliedRef.current = false;
      setSelectedPipeIds([]);
      setCommittedPipeIds([]);
      dispatch(getAllPipeDetails({ projectId }));
    }
  }, [dispatch, projectId]);

  const pipeOptions: PipeOption[] = useMemo(() => {
    if (!pipeDetails || typeof pipeDetails !== 'object') return [];
    const list: PipeOption[] = [];
    Object.values(pipeDetails).forEach((setup: any) => {
      if (setup.pipeSizes && Array.isArray(setup.pipeSizes)) {
        setup.pipeSizes.forEach((pipe: any) => {
          const displayName =
            setup.projectSubType === 'none' && setup.projectType !== 'fabrication'
              ? `${setup.projectType === 'pipeline' ? 'Pipeline' : setup.projectType === 'others' ? 'Others' : setup.projectType} ${pipe.pipeName || 'Pipe'} (${pipe.pipeSize ?? ''} x ${pipe.wallThickness ?? ''})`
              : `${setup.projectSubType === 'buckleArrestor' ? 'Buckle Arrestor' : setup.projectSubType === 'pipeInPipe' ? 'Pipe in Pipe' : setup.projectSubType === 'bulkhead' ? 'Bulk Head' : setup.projectSubType || 'Pipe'} ${pipe.pipeName || 'Pipe'} (${pipe.pipeSize ?? ''} x ${pipe.wallThickness ?? ''})`;
          list.push({ pipeId: pipe.pipeId, displayName });
        });
      }
    });
    return list;
  }, [pipeDetails]);

  useEffect(() => {
    if (pipeOptions.length > 0 && !defaultPipeAppliedRef.current) {
      defaultPipeAppliedRef.current = true;
      const first = [String(pipeOptions[0].pipeId)];
      setSelectedPipeIds(first);
      setCommittedPipeIds(first);
    }
  }, [pipeOptions]);

  const pipeSelectorOptions = useMemo(
    () => pipeOptions.map((p) => ({ value: String(p.pipeId), label: p.displayName })),
    [pipeOptions],
  );

  return (
    <div className="min-w-0 space-y-5">
      <h2 className="font-heading text-lg font-semibold text-blueprint-blue">Analytics</h2>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {showPipeToolbar ? (
            <>
              <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                  <Label className="shrink-0 whitespace-nowrap text-[12px] font-medium text-text-grey sm:mr-1">
                    Pipe Size:
                  </Label>
                  <div className="min-w-0 w-full max-w-[520px] sm:w-[400px]">
                    <MultiSelect
                      options={pipeSelectorOptions}
                      selected={selectedPipeIds}
                      onChange={setSelectedPipeIds}
                      placeholder="Select pipe size(s)"
                      className="w-full"
                      chipLayout="wrap"
                      onOpenChange={(open) => {
                        if (!open) setCommittedPipeIds([...selectedPipeIdsRef.current]);
                      }}
                    />
                  </div>
                </div>
                {showFiltersToggle ? (
                  <button
                    type="button"
                    data-testid="analytics-filters-toggle"
                    aria-expanded={filtersOpen}
                    onClick={() => setFiltersOpen((open) => !open)}
                    className={clsx(
                      'inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border px-3 text-[12px] font-medium transition',
                      filtersOpen
                        ? 'border-heritage-grey bg-page-bg text-blueprint-blue'
                        : 'border-[#E2E5EA] bg-white text-text-grey hover:border-blueprint-blue hover:text-blueprint-blue',
                    )}
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
                    Filters
                    <ChevronDown
                      className={clsx(
                        'h-3.5 w-3.5 text-heritage-grey transition-transform',
                        filtersOpen && 'rotate-180',
                      )}
                      aria-hidden
                    />
                  </button>
                ) : null}
              </div>
              {selectedPipeIds.length > 0 ? (
                <div className="flex min-w-0 w-full flex-col gap-2 sm:flex-row sm:items-start">
                  <Label
                    className="invisible hidden shrink-0 whitespace-nowrap text-[12px] font-medium sm:mr-1 sm:block"
                    aria-hidden
                  >
                    Pipe Size:
                  </Label>
                  <MultiSelectChips
                    options={pipeSelectorOptions}
                    selected={selectedPipeIds}
                    layout="ellipsis"
                    onRemove={(value) => {
                      const next = selectedPipeIds.filter((id) => id !== value);
                      setSelectedPipeIds(next);
                      setCommittedPipeIds(next);
                    }}
                    className="min-w-0 w-full flex-1"
                  />
                </div>
              ) : null}
            </>
          ) : (
            <div className="hidden lg:block" aria-hidden />
          )}
        </div>

        {SHOW_LEGACY_ANALYTICS_VIEWS ? (
          <div className="flex shrink-0 items-center gap-2">
            <div
              role="tablist"
              aria-label="Analytics view"
              className="inline-flex gap-1 rounded-lg border border-[#E2E5EA] bg-page-bg p-[3px]"
            >
              {PRIMARY_VIEWS.map((view) => {
                const isActive = activeView === view.id;
                return (
                  <button
                    key={view.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveView(view.id)}
                    className={clsx(
                      'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-[12px] transition-all duration-150 sm:text-[13px]',
                      isActive
                        ? 'bg-white font-semibold text-blueprint-blue shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
                        : 'font-medium text-heritage-grey hover:text-blueprint-blue',
                    )}
                  >
                    {view.label}
                  </button>
                );
              })}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#E2E5EA] bg-white text-heritage-grey hover:border-blueprint-blue hover:text-blueprint-blue">
                <MoreHorizontal className="h-4 w-4" aria-hidden />
                <span className="sr-only">More analytics views</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {OVERFLOW_VIEWS.map((view) => (
                  <DropdownMenuItem
                    key={view.id}
                    onClick={() => setActiveView(view.id)}
                    className="cursor-pointer"
                  >
                    {view.label}
                    {view.comingSoon ? (
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-heritage-grey">
                        Soon
                      </span>
                    ) : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>

      {!SHOW_LEGACY_ANALYTICS_VIEWS ? (
        <CombinedAnalyticsHost
          project={project}
          pipeIds={committedPipeIds}
          pipeOptions={pipeOptions}
          filtersOpen={filtersOpen}
          onFiltersOpenChange={setFiltersOpen}
        />
      ) : (
        <>
          {activeView === 'ndt' && <NDTDashboard project={project} />}
          {activeView === 'weld' && (
            <WeldDashboard
              project={project}
              pipeIds={committedPipeIds}
              pipeOptions={pipeOptions}
              onPipeIdsChange={(ids) => {
                setSelectedPipeIds(ids);
                setCommittedPipeIds(ids);
              }}
              filtersOpen={filtersOpen}
              onFiltersOpenChange={setFiltersOpen}
            />
          )}
          {activeView === 'combined' && (
            <CombinedAnalyticsHost
              project={project}
              pipeIds={committedPipeIds}
              pipeOptions={pipeOptions}
              filtersOpen={filtersOpen}
              onFiltersOpenChange={setFiltersOpen}
            />
          )}
          {activeView === 'executive' && (
            <ComingSoon title="Executive Summary" description={COMING_SOON_COPY.executive} />
          )}
        </>
      )}
    </div>
  );
};
